import { Component, computed, input, Input, OnInit, Signal, inject } from '@angular/core';
import { Location, NgClass, NgOptimizedImage } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { OffcanvasService } from '../../../../_services/offcanvas-service/offcanvas.service';
import { ToastService } from '../../../../_services/toast-service/toast.service';
import { CardsApiService } from '../../../../_services/cards-service/cards-api/cards-api.service';
import {
  Card,
  CardBlockReason,
  CardBlockRequest,
  CardResponse,
  CardType,
} from '../../../../_interfaces/cards.interface';
import { Store } from '@ngxs/store';
import { AppSelectors } from '../../../../store/app/app.selectors';
import { BoFormsModule, FormComponent } from '@bo/ng-forms';
import { Method } from '@bo/ng-api';
import { FieldComponent } from '../../forms/form-field/form-field.component';
import { SubmitComponent } from '../../forms/submit/submit.component';
import { LoaderComponent } from '../../loader/loader.component';
import { OffcanvasBaseComponent } from '../../offcanvas/offcanvas-base/offcanvas-base.component';
import { cardBlockSelectOptions } from '../_utils/card-utils';

@Component({
  selector: 'app-block-card',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    NgClass,
    NgOptimizedImage,
    BoFormsModule,
    FieldComponent,
    SubmitComponent,
    LoaderComponent,
    OffcanvasBaseComponent,
  ],
  templateUrl: './block-card.component.html',
})
export class BlockCardComponent extends FormComponent<CardResponse> implements OnInit {
  readonly offcanvasService = inject(OffcanvasService);
  readonly toastService = inject(ToastService);
  readonly cardsApiService = inject(CardsApiService);
  readonly location = inject(Location);
  readonly store = inject(Store);
  readonly userEmail$ = this.store.select(AppSelectors.userEmail);

  @Input({ required: true }) cardDetails!: Card;
  @Input({ required: true }) title: string | undefined;

  cardType = input.required<CardType>();
  isPersonalCard = computed(() => this.cardType() === CardType.Personal);
  isSubscriptionCard = computed(() => this.cardType() === CardType.Subscription);

  protected readonly selectOptions = computed(() => {
    if (this.isPersonalCard()) return cardBlockSelectOptions;
    else if (this.isSubscriptionCard())
      return cardBlockSelectOptions.filter(
        (option) => option.value !== CardBlockReason.Stolen && option.value !== CardBlockReason.Lost,
      );
    else return [];
  });

  protected readonly CardBlockReason = CardBlockReason;
  protected fields: string[] = ['block_reason_other'];

  protected signedInUserEmail?: string;
  private cardId!: number;
  protected isOther: boolean = false;

  protected returnUrl: Signal<string> = computed<string>(() => {
    if (this.isPersonalCard()) return '/cards/employee-cards';
    if (this.isSubscriptionCard()) return '/cards/subscription-cards';
    return '';
  });

  constructor() {
    super();
    // Check who the logged-in user is
    this.userEmail$.subscribe((email) => {
      this.signedInUserEmail = email;
    });
  }

  override createForm() {
    const options$ = this.cardsApiService.getOptions(this.cardsApiService.URL_CARD_BLOCK + '/', Method.POST);
    this.subscriptions.add(
      this.buildFromOptions(options$).subscribe({
        next: () => {
          this.form.get('block_reason_other')?.disable();
        },
      }),
    );
  }

  override ngOnInit() {
    super.ngOnInit();
    // Check if cardDetails has been initialized before accessing its properties
    if (!this.cardDetails?.id) {
      return;
    }

    this.cardId = this.cardDetails.id;
  }

  close() {
    this.offcanvasService.close(this.returnUrl());
  }

  navigateBack() {
    this.location.back();
  }

  onSelectOption(value: CardBlockReason) {
    for (const option of this.selectOptions()) {
      option.checked = option.value === value;
    }
    this.setTextField();
  }

  setTextField() {
    if (this.getSelectedOption()?.value === 2) {
      this.form.get('block_reason_other')?.enable();
    } else {
      this.form.get('block_reason_other')?.disable();
    }
  }

  getSelectedOption() {
    return this.selectOptions().find((option) => Number(option.checked));
  }

  override submitData(formData: CardBlockRequest) {
    formData.card = this.cardId;
    if (this.getSelectedOption()) formData.block_reason = Number(this.getSelectedOption()?.value);
    if (this.getSelectedOption()?.value === 2) {
      this.isOther = true;
    }
    return this.cardsApiService.blockCard(formData);
  }

  override handleSubmitSuccess(): void {
    this.toastService.show(
      `You have blocked ${this.isPersonalCard() ? 'an employee' : 'a subscription'} card.`,
      'iconoir-check-circle',
    );
    this.offcanvasService.offCanvasNavigate(`${this.returnUrl()}/view/${this.cardId}`);
  }

  override handleSubmitError(err: { error: { card?: [] } }): void {
    let errorMsg;
    if (err.error.card) errorMsg = 'This card is invalid.';
    this.toastService.show(`Something went wrong. ${errorMsg ?? ''}`, 'iconoir-xmark');
  }
}
